/**
 * Onboarding Welcome Page
 *
 * Welcomes the user and, on continue, automatically starts the Full Setup
 * onboarding flow. Displays capability grid showcasing platform features.
 *
 * References:
 * - specs/010-onboarding-path/spec.md
 * - phase1-mvp/stories/STORY-028-choose-your-path-page.md
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CapabilityGrid } from '../components/onboarding/CapabilityGrid';
import { useConsent } from '../hooks/useConsent';
import { useAnalytics } from '../hooks/useAnalytics';
import { useAuth } from '../stores/use-auth';
import { OnboardingLayout } from '../components/onboarding/OnboardingLayout';
import { StepNavigationFooter } from '../components/onboarding/StepNavigationFooter';

interface OnboardingPathPageProps {
  onSelect?: (path: string) => void;
}

export default function OnboardingPathPage({ onSelect }: OnboardingPathPageProps) {
  const { t } = useTranslation(['onboarding', 'common']);
  const { user } = useAuth();
  const { consent } = useConsent();
  const analytics = useAnalytics(consent, user?.userId);
  const selected = 'full' as const;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pageLoadTime] = useState(Date.now());

  // Track page view on mount
  useEffect(() => {
    if (analytics.trackEvent) {
      analytics.trackEvent('onboarding_choose_path_viewed', {
        timestamp: new Date().toISOString(),
      });
    }
  }, [analytics]);

  const handleContinue = async () => {
    setIsSubmitting(true);

    // Track selection with time on page
    const timeOnPage = Math.round((Date.now() - pageLoadTime) / 1000);
    if (analytics.trackEvent) {
      analytics.trackEvent('onboarding_path_selected', {
        path: selected,
        time_on_page_seconds: timeOnPage,
      });
    }

    try {
      // Call parent handler (which calls API)
      await onSelect?.(selected);
    } catch (error) {
      console.error('Failed to save path selection:', error);
      // Error handling is done by parent component
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <OnboardingLayout
      title={t('onboarding:path.welcome_title', 'Welcome to CodeMerlin')}
      description={t(
        'onboarding:path.welcome_description',
        "Let's get your organization set up. You can always configure additional features later."
      )}
    >
      {/* Slight uniform scale so full page fits above the fold; keeps original card/copy styling */}
      <div className="w-full origin-top [zoom:0.92] min-[1280px]:[zoom:0.95] max-[1000px]:[zoom:0.88]">
        {/* Capability Grid */}
        <div className="mb-5">
          <h2 className="mb-3 text-center text-lg font-semibold">
            {t('onboarding:path.capabilities_title', 'What CodeMerlin Can Do')}
          </h2>
          <CapabilityGrid />
        </div>

        {/* Reassurance Text */}
        <div className="mb-6 text-center">
          <p className="text-sm text-muted-foreground">
            💡{' '}
            {t(
              'onboarding:path.reassurance',
              'You can always configure these features later in Settings.'
            )}
          </p>
        </div>

        <StepNavigationFooter
          onContinue={handleContinue}
          isLoading={isSubmitting}
          isContinueDisabled={false}
        />
      </div>
    </OnboardingLayout>
  );
}
